import _ from 'lodash';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { TranslationWithHTML } from '@/core/ContainerWithHTML.atom';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import {
  getFormFieldProps,
  getFormFieldWrapperProps,
  getValidationFunction,
} from '@/formbuilder/formbuilder.utilities';
import { TextArea, TextField } from '@seeqdev/qomponents';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from 'react';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface TextFieldIF extends ValidatingFormComponent<string | number> {
  component: 'TextFieldFormComponent';
  /** Styles the input or textarea. */
  xstyle?: StyleXStyles;
  id?: string;
  size: 'sm' | 'lg';
  placeholder?: string;
  autoComplete?: string;
  type?: 'number' | 'text' | 'email' | 'password';
  step?: number;
  maxLength?: number;
  minLength?: number;
  max?: number;
  min?: number;
  rows?: number;
  as?: string;
  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
  onKeyDown?: (key: string, value: string) => void;
  blurOnEnter?: boolean;
  disabled?: boolean;
  skipMemo?: boolean;
  autoFocus?: boolean;
  displayError?: boolean;
  customErrorText?: string;
  labelIcon?: string;
  labelIconAction?: () => void;
  labelIconTooltip?: string;
  labelClassNames?: string;
  labelIconClassNames?: string;
  translationParams?: { [key: string]: string | number | ReactNode };
}

export const TextFieldFormComponent = (props: TextFieldIF) => {
  const {
    name,
    id,
    extendValidation,
    validation,
    placeholder,
    onChange,
    onBlur,
    onKeyDown,
    blurOnEnter,
    type = 'text',
    step,
    testId = 'formControl',
    required,
    minLength,
    maxLength,
    min,
    max,
    customErrorText,
    labelIcon,
    labelIconAction,
    labelIconTooltip,
    labelClassNames,
    labelIconClassNames,
    translationParams,
  } = props;

  const { t } = useTranslation();

  const defaultValidation = (value: string | number) => {
    const strValue = `${value ?? ''}`;
    const numericValue = Number(value);

    return (
      (required && _.isEmpty(_.trim(strValue))) ||
      (minLength ? strValue.length < minLength : false) ||
      (maxLength ? strValue.length > maxLength : false) ||
      (min ? isNaN(numericValue) || numericValue < min : false) ||
      (max ? isNaN(numericValue) || numericValue > max : false)
    );
  };

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const formState = useForm().getState();
  const { errors, hasValidationErrors, modified, submitFailed } = useFormState({
    subscription: { errors: true, hasValidationErrors: true, modified: true, submitFailed: true },
  });
  const showError =
    _.has(errors, name) &&
    hasValidationErrors &&
    ((modified && modified[name]) || submitFailed || !!props.defaultProvided);
  const labelWithHtml = (
    <TranslationWithHTML translationKey={props.label as string} translationParams={translationParams} />
  );

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props)}
      id={id ?? `${testId}-wrapper`}
      testId={`${testId}-wrap`}
      label={translationParams ? labelWithHtml : props.label}
      labelIcon={labelIcon}
      labelIconAction={labelIconAction}
      labelIconTooltip={labelIconTooltip}
      labelIconClassNames={labelIconClassNames}
      labelClassNames={labelClassNames}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const properProps = _.assign({}, getFormFieldProps(formState, input, meta, props), {
            // match previous behavior by always translating the provided placeholder key
            placeholder: t(placeholder as string),
            value: input.value,
            onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              input.onChange(e.target.value);
              onChange?.(e.target.value);
            },
            onBlur: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (_.isFunction(onBlur)) {
                onBlur(e.target.value);
              }
            },
            onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (blurOnEnter && e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              } else {
                onKeyDown?.(e.key, e.currentTarget.value);
              }
            },
            onFocus: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => e.target.select(),
          });

          return props.as === 'textarea' && (props.rows || 0) > 1 ? (
            <TextArea
              id={properProps.id}
              name={properProps.name}
              onChange={properProps.onChange}
              onKeyUp={properProps.onKeyUp}
              onKeyDown={properProps.onKeyDown}
              onFocus={properProps.onFocus}
              onBlur={properProps.onBlur}
              placeholder={properProps.placeholder}
              value={properProps.value}
              cols={properProps.cols}
              showError={properProps.showError}
              readonly={properProps.readonly}
              disabled={properProps.disabled}
              extraClassNames={`width-maximum ${properProps.extraClassNames}`}
              testId={testId}
              xstyle={props.xstyle}
              rows={properProps.rows}
              autoFocus={properProps.autoFocus}
            />
          ) : (
            <TextField
              xstyle={props.xstyle}
              id={properProps.id}
              name={properProps.name}
              onChange={properProps.onChange}
              onKeyUp={properProps.onKeyUp}
              onKeyDown={properProps.onKeyDown}
              onFocus={properProps.onFocus}
              onBlur={properProps.onBlur}
              placeholder={properProps.placeholder}
              size={props.size}
              value={properProps.value}
              type={type}
              autoComplete={properProps.autoComplete}
              maxLength={properProps.maxLength}
              minLength={properProps.minLength}
              min={properProps.min}
              max={properProps.max}
              ref={properProps.ref}
              inputGroup={properProps.inputGroup}
              showError={properProps.showError}
              readonly={properProps.readonly}
              disabled={properProps.disabled}
              extraClassNames={`width-maximum ${properProps.extraClassNames}`}
              testId={testId}
              step={step}
              autoFocus={properProps.autoFocus}
              required={properProps.showError && properProps.required}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
